/**
 * Flight Service
 * API calls for flight-related operations
 *
 * Note: This service handles BOTH:
 * - Legacy FlightLog entries (simple flight logging via /flights endpoint)
 * - Trip-based Flight status updates (via /trips/flights endpoint)
 */

import { api } from './api';
import type { FlightLog, MonthlySummary, PayCalculation, CreateFlightLogRequest, FlightStatus } from '@/types/flight';

export const flightService = {
  /**
   * Get all flight logs (optionally filtered by userId)
   * Returns legacy FlightLog entries
   */
  async getFlights(userId?: number): Promise<FlightLog[]> {
    const endpoint = userId ? `/flights?userId=${userId}` : '/flights';
    const response = await api.get<{ success: boolean; count: number; data: FlightLog[] }>(endpoint);
    return response.data;
  },

  /**
   * Get a single flight log by ID
   */
  async getFlight(id: number): Promise<FlightLog> {
    const response = await api.get<{ success: boolean; data: FlightLog }>(`/flights/${id}`);
    return response.data;
  },

  /**
   * Get monthly summary for a user
   */
  async getMonthlySummary(
    userId: number,
    year: number,
    month: number
  ): Promise<MonthlySummary> {
    const response = await api.get<{ success: boolean; data: MonthlySummary }>(`/flights/summary/${userId}/${year}/${month}`);
    return response.data;
  },

  /**
   * Get pay calculation for a user
   */
  async getPayCalculation(userId: number, year?: number, month?: number): Promise<PayCalculation> {
    let endpoint = `/flights/pay/${userId}`;
    if (year && month) {
      endpoint += `?year=${year}&month=${month}`;
    }
    const response = await api.get<{ success: boolean; data: PayCalculation }>(endpoint);
    return response.data;
  },

  /**
   * Create a new flight log entry (legacy system)
   */
  async createFlight(data: CreateFlightLogRequest): Promise<FlightLog> {
    const response = await api.post<{ success: boolean; data: FlightLog }>('/flights', data);
    return response.data;
  },

  /**
   * Update a flight log entry
   */
  async updateFlight(id: number, data: Partial<CreateFlightLogRequest>): Promise<FlightLog> {
    const response = await api.put<{ success: boolean; data: FlightLog }>(`/flights/${id}`, data);
    return response.data;
  },

  /**
   * Delete a flight log entry
   */
  async deleteFlight(id: number): Promise<void> {
    await api.delete<{ success: boolean; message: string }>(`/flights/${id}`);
  },

  /**
   * Update status of a Trip-based Flight
   * This is for Trip flights, not legacy FlightLog entries
   */
  async updateFlightStatus(flightId: number, status: FlightStatus): Promise<{ id: number; status: FlightStatus }> {
    const response = await api.put<{ success: boolean; data: { id: number; status: FlightStatus } }>(
      `/trips/flights/${flightId}/status`,
      { status }
    );
    return response.data;
  },
};
